import { useEffect, useState } from 'react';
import Select from 'react-select';
import api from '../../api/axios';
import { Box, Button, Typography } from '@mui/material';

type ModelOption = {
  value: string;
  label: string;
  brandId: string;
  brandName: string;
};

interface Brand {
  _id: string;
  name: string;
  iconKey?: string | null;
}
interface Model {
  _id: string;
  name: string;
  brandId: string;
}

interface Props {
  value: string[];
  onChange: (value: string[]) => void;
  withSelectAllPerBrand?: boolean;
}

const BrandModelSelector = ({ value, onChange, withSelectAllPerBrand = true }: Props) => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchBrands = async () => {
      const res = await api.get<Brand[]>('/api/admin/brands-car');
      setBrands(res.data);
    };
    fetchBrands();
  }, []);

  useEffect(() => {
    const fetchModels = async () => {
      setLoading(true);
      const allModels: Model[] = [];

      for (const brand of brands) {
        const res = await api.get<Model[]>(`/api/admin/by-brand/${brand._id}`);
        allModels.push(...res.data);
      }

      setModels(allModels);
      setLoading(false);
    };

    if (brands.length) fetchModels();
  }, [brands]);

  const groupedOptions = brands.map(brand => {
    const brandModels = models.filter(m => m.brandId === brand._id);
    const options: ModelOption[] = brandModels.map(model => ({
      value: model._id,
      label: model.name,
      brandId: brand._id,
      brandName: brand.name,
    }));

    return {
      label: brand.name,
      options,
    };
  });

  const allOptionsFlat: ModelOption[] = groupedOptions.flatMap(g => g.options);

  const handleSelectAllFromBrand = (brandId: string) => {
    const modelIds = models
      .filter(m => m.brandId === brandId)
      .map(m => m._id);
    onChange([...new Set([...value, ...modelIds])]);
  };

  return (
    <Box mt={2}>
      <Typography fontSize={14} fontWeight={500} mb={1}>
        Марки / Модели
      </Typography>

      <Select
        isMulti
        isLoading={loading}
        options={groupedOptions}
        value={allOptionsFlat.filter(opt => value.includes(opt.value))}
        onChange={selected => onChange((selected as ModelOption[]).map(opt => opt.value))}
        placeholder="Выберите модели"
        filterOption={(option, rawInput) => {
          const input = rawInput.toLowerCase().trim();
          if (!input) return true;

          const tokens = input.split(/\s+/).filter(Boolean);

          const brandName = (option.data as ModelOption).brandName?.toLowerCase() || '';
          const modelName = option.label.toLowerCase();
          const haystack = `${brandName} ${modelName}`;

          return tokens.every(token => haystack.includes(token));
        }}
      />


      {withSelectAllPerBrand && (
        <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
          {brands.map(brand => (
            <Button
              key={brand._id}
              variant="outlined"
              size="small"
              onClick={() => handleSelectAllFromBrand(brand._id)}
            >
              {`Выбрать все ${brand.name}`}
            </Button>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default BrandModelSelector;
